import type { RouteObject } from 'react-router';

import { Outlet } from 'react-router';
import { lazy, Suspense } from 'react';
import { AuthGuard } from '@classroom-ai/ui/auth/guard';
import { usePathname } from '@classroom-ai/ui/routes/hooks';
import { LoadingScreen } from '@classroom-ai/ui/components/loading-screen';

import { CONFIG } from 'src/global-config';
import { DashboardLayout } from 'src/layouts/dashboard';

// ----------------------------------------------------------------------

const IndexPage = lazy(() => import('src/pages/dashboard/one'));

const SchoolsListPage = lazy(() => import('src/pages/dashboard/schools/list'));
const SchoolsCreatePage = lazy(() => import('src/pages/dashboard/schools/create'));
const SchoolsDetailPage = lazy(() => import('src/pages/dashboard/schools/detail'));
const SchoolsEditPage = lazy(() => import('src/pages/dashboard/schools/edit'));
const CourseDetailPage = lazy(() => import('src/pages/dashboard/schools/course-detail'));
const CreateCoursePage = lazy(() => import('src/pages/dashboard/schools/create-course'));
const CreateClassPage = lazy(() => import('src/pages/dashboard/schools/create-class'));
const CreateSchoolYearPage = lazy(() => import('src/pages/dashboard/schools/create-school-year'));
const SchoolYearDetailPage = lazy(() => import('src/pages/dashboard/schools/school-year-detail'));
const CreateAcademicPeriodPage = lazy(
  () => import('src/pages/dashboard/schools/create-academic-period')
);
const CreateSpecialDayPage = lazy(() => import('src/pages/dashboard/schools/create-special-day'));
const CreateDailySchedulePage = lazy(
  () => import('src/pages/dashboard/schools/create-daily-schedule')
);
const DailyScheduleDetailPage = lazy(
  () => import('src/pages/dashboard/schools/daily-schedule-detail')
);
const ClassDetailPage = lazy(() => import('src/pages/dashboard/schools/class-detail'));
const StudentDetailPage = lazy(() => import('src/pages/dashboard/schools/student-detail'));
const DistrictsListPage = lazy(() => import('src/pages/dashboard/districts/list'));
const DistrictsCreatePage = lazy(() => import('src/pages/dashboard/districts/create'));
const DistrictsDetailPage = lazy(() => import('src/pages/dashboard/districts/detail'));
const UsersListPage = lazy(() => import('src/pages/dashboard/users/list'));
const UsersCreatePage = lazy(() => import('src/pages/dashboard/users/create'));
const UsersEditPage = lazy(() => import('src/pages/dashboard/users/edit'));
const PermissionGroupsPage = lazy(() => import('src/pages/dashboard/access/permission-groups'));

// ----------------------------------------------------------------------

function SuspenseOutlet() {
  const pathname = usePathname();
  return (
    <Suspense key={pathname} fallback={<LoadingScreen />}>
      <Outlet />
    </Suspense>
  );
}

const dashboardLayout = () => (
  <DashboardLayout>
    <SuspenseOutlet />
  </DashboardLayout>
);

export const dashboardRoutes: RouteObject[] = [
  {
    path: 'dashboard',
    element: CONFIG.auth.skip ? dashboardLayout() : <AuthGuard>{dashboardLayout()}</AuthGuard>,
    children: [
      { element: <IndexPage />, index: true },
      {
        path: 'schools',
        children: [
          { path: 'list', element: <SchoolsListPage /> },
          { path: 'create', element: <SchoolsCreatePage /> },
          { path: ':id', element: <SchoolsDetailPage /> },
          { path: ':id/edit', element: <SchoolsEditPage /> },
          { path: ':id/students/:uid', element: <StudentDetailPage /> },
          { path: ':id/courses/create', element: <CreateCoursePage /> },
          { path: ':id/courses/:courseId', element: <CourseDetailPage /> },
          { path: ':id/courses/:courseId/classes/create', element: <CreateClassPage /> },
          { path: ':id/courses/:courseId/classes/:classId', element: <ClassDetailPage /> },
          { path: ':id/school-years/create', element: <CreateSchoolYearPage /> },
          { path: ':id/school-years/:schoolYearId', element: <SchoolYearDetailPage /> },
          {
            path: ':id/school-years/:schoolYearId/periods/create',
            element: <CreateAcademicPeriodPage />,
          },
          {
            path: ':id/school-years/:schoolYearId/special-days/create',
            element: <CreateSpecialDayPage />,
          },
          {
            path: ':id/school-years/:schoolYearId/daily-schedules/create',
            element: <CreateDailySchedulePage />,
          },
          {
            path: ':id/school-years/:schoolYearId/daily-schedules/:scheduleId',
            element: <DailyScheduleDetailPage />,
          },
        ],
      },
      {
        path: 'districts',
        children: [
          { path: 'list', element: <DistrictsListPage /> },
          { path: 'create', element: <DistrictsCreatePage /> },
          { path: ':id', element: <DistrictsDetailPage /> },
        ],
      },
      {
        path: 'users',
        children: [
          { path: 'list', element: <UsersListPage /> },
          { path: 'create', element: <UsersCreatePage /> },
          { path: ':id/edit', element: <UsersEditPage /> },
        ],
      },
      {
        path: 'access',
        children: [{ path: 'groups', element: <PermissionGroupsPage /> }],
      },
    ],
  },
];
